import { parsePath } from './PathUtils'
import resolvePathname from './resolve-pathname'
import { Location } from './types'

const bDebug = false

export function createLocation<S = unknown>(
  path: string | Partial<Location<S>>,
  state?: S,
  key?: string,
  currentLocation?: Location<S>
): Location<S> {
  let location: Location<S>

  bDebug && console.log('XXX createLocation #1', { path, location, state, key, currentLocation })

  if (typeof path === 'string') {
    // Two-arg form: push(path, state)
    location = parsePath(path) as Location<S>
    location.state = state
  } else {
    // One-arg form: push(location)
    location = { ...path } as Location<S>

    if (location.pathname === undefined) location.pathname = ''

    // Ensure search starts with '?' if not empty
    if (location.search) {
      if (location.search.charAt(0) !== '?') location.search = `?${location.search}`
    } else {
      location.search = ''
    }

    // Ensure hash starts with '#' if not empty
    if (location.hash) {
      if (location.hash.charAt(0) !== '#') location.hash = `#${location.hash}`
    } else {
      location.hash = ''
    }

    // Assign state if it's provided
    if (state !== undefined && location.state === undefined) location.state = state
  }

  // Decode pathname safely
  try {
    location.pathname = decodeURI(location.pathname)
  } catch (e) {
    if (e instanceof URIError) {
      throw new URIError(
        `Pathname "${location.pathname}" could not be decoded. ` +
          `This is likely caused by an invalid percent-encoding.`
      )
    } else {
      throw e
    }
  }

  // Assign key if provided
  if (key) location.key = key

  bDebug && console.log('XXX createLocation #2', { path, location, state, key, currentLocation })

  // Handle relative paths based on the current location
  if (currentLocation) {
    // Resolve relative pathname if necessary
    if (!location.pathname) {
      location.pathname = currentLocation.pathname
    } else if (location.pathname.charAt(0) !== '/') {
      location.pathname = resolvePathname(location.pathname, currentLocation.pathname)
    }
  }

  // If no currentLocation and no pathname, set pathname to '/'
  if (!currentLocation && !location.pathname) {
    location.pathname = '/'
  }

  return location
}

export default function deepEqual(x: any, y: any): boolean {
  return x && y && typeof x === 'object' && typeof y === 'object'
    ? Object.keys(x).length === Object.keys(y).length &&
        Object.keys(x).reduce((isEqual, key) => isEqual && deepEqual(x[key], y[key]), true)
    : x === y
}

export function locationsAreEqual<S = unknown>(a: Location<S>, b: Location<S>): boolean {
  return (
    a.pathname === b.pathname &&
    a.search === b.search &&
    a.hash === b.hash &&
    a.key === b.key &&
    deepEqual(a.state, b.state)
  )
}
